import { useCallback, useState, useRef } from 'react';
import { useUpdatePostMutation } from '../redux/adminApiSlice';
import { Post } from '../types/interfaces';

export interface UsePostMediaOptions {
  postId: string;
  onUpdate?: (updatedPost: Post) => void;
  onError?: (error: any) => void;
}

export interface UsePostMediaReturn {
  handleFileSelect: (file: File) => Promise<void>;
  handleDeleteMedia: () => Promise<void>;
  triggerFileInput: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  isUploading: boolean;
  isDeleting: boolean;
}

/**
 * Hook for managing post media (upload, edit, delete)
 * Uses updatePost mutation to update media field
 * Note: This converts files to base64 data URLs. For production, consider using a proper file upload service.
 */
export const usePostMedia = ({ postId, onUpdate, onError }: UsePostMediaOptions): UsePostMediaReturn => {
  const [updatePost, { isLoading: isUploading }] = useUpdatePostMutation();
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(async (file: File) => {
    if (!postId) return;

    return new Promise<void>((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        try {
          const dataUrl = reader.result as string;
          const isVideo = file.type.startsWith('video/');
          const mediaType = isVideo ? 'video' : 'photo';
          
          // Get dimensions
          const getDimensions = (): Promise<{ width: number; height: number; aspectRatio: number }> => {
            return new Promise((resolve) => {
              if (isVideo) {
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.onloadedmetadata = () => {
                  resolve({
                    width: video.videoWidth,
                    height: video.videoHeight,
                    aspectRatio: video.videoWidth / video.videoHeight,
                  });
                };
                video.src = dataUrl;
              } else {
                const img = new Image();
                img.onload = () => {
                  resolve({
                    width: img.width,
                    height: img.height,
                    aspectRatio: img.width / img.height,
                  });
                };
                img.src = dataUrl;
              }
            });
          };

          const { width, height, aspectRatio } = await getDimensions();
          
          const mediaUpdate: Partial<Post['media']> = {
            type: mediaType,
            [isVideo ? 'video' : 'photo']: dataUrl,
            [isVideo ? 'photo' : 'video']: undefined,
            isVisible: true,
            width,
            height,
            aspectRatio,
          };

          const updatedPost = await updatePost({
            id: postId,
            updates: { media: mediaUpdate as Post['media'] },
          }).unwrap();

          if (onUpdate) {
            onUpdate(updatedPost);
          }
          resolve();
        } catch (error) {
          console.error('Error updating post media:', error);
          if (onError) {
            onError(error);
          } else {
            alert('Failed to upload media');
          }
          reject(error);
        }
      };

      reader.onerror = () => {
        const error = new Error('Failed to read file');
        if (onError) {
          onError(error);
        } else {
          alert('Failed to read file');
        }
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }, [postId, updatePost, onUpdate, onError]);

  const handleDeleteMedia = useCallback(async () => {
    if (!postId) return;

    try {
      setIsDeleting(true);
      const updatedPost = await updatePost({
        id: postId,
        updates: { media: undefined } as Partial<Post>,
      }).unwrap();

      if (onUpdate) {
        onUpdate(updatedPost);
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      if (onError) {
        onError(error);
      } else {
        alert('Failed to delete media');
      }
    } finally {
      setIsDeleting(false);
    }
  }, [postId, updatePost, onUpdate, onError]);

  return {
    handleFileSelect,
    handleDeleteMedia,
    triggerFileInput,
    fileInputRef,
    isUploading,
    isDeleting,
  };
};
